import React, { FunctionComponent, useEffect, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import { useSnackbar } from 'notistack';

import * as API from '../../services/API';

import PopupDialog from '../shared/PopupDialog';
import Table from '../shared/Table';
import CenteredSpinner from '../shared/CenteredSpinner';
import OverflowTooltip from '../shared/OverflowTooltip';

interface ITagBlockerItem {
  modelInstanceName: string;
  scenarioName: string | null;
  assignmentType: 'Promotion' | 'Subscription';
}

interface ITagBlockersResponse {
  tagId: number;
  tagName: string;
  items: ITagBlockerItem[];
  total: number;
  truncated: boolean;
}

interface ITagBlockersDialogProps {
  open: boolean;
  clientId?: number;
  tagId: number | null;
  tagName: string;
  onClose: () => void;
}

const BLOCKER_COLUMNS: ColDef<ITagBlockerItem>[] = [
  {
    headerName: 'Model Instance',
    field: 'modelInstanceName',
    flex: 1,
    cellRenderer: OverflowTooltip,
  },
  {
    headerName: 'Scenario',
    field: 'scenarioName',
    flex: 1,
    valueGetter: (params) => params.data?.scenarioName || '—',
    cellRenderer: OverflowTooltip,
  },
  {
    headerName: 'Assignment',
    field: 'assignmentType',
    flex: 1,
    cellRenderer: OverflowTooltip,
  },
];

// Keeps the downloaded filename under the OS limit and strips invalid chars.
const MAX_FILENAME_TAG_LENGTH = 100;

const sanitizeForFilename = (value: string): string =>
  value
    .replace(/[\\/:*?"<>|]/g, '_')
    .trim()
    .slice(0, MAX_FILENAME_TAG_LENGTH);

// Prevent spreadsheet formula injection by prefixing cells that start with
// formula indicator characters with a single quote, which spreadsheet apps
// treat as an escape sequence. Then apply standard CSV quoting for safety.
export const escapeCell = (value: string): string => {
  if (/^[=+\-@\t\r]/.test(value)) {
    value = `'${value}`;
  }
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
};

// Small, dependency-free CSV export - the row shape here is flat and uniform,
// so a CSV blob built client-side is far lighter than pulling in an xlsx
// library for what's ultimately a plain list (CLD-2988).
const downloadBlockersCsv = (
  tagName: string,
  items: ITagBlockerItem[]
): void => {
  const escapeCell = (value: string): string =>
    /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

  const header = ['Model Instance', 'Scenario', 'Assignment'];
  const rows = items.map((item) =>
    [item.modelInstanceName, item.scenarioName || '', item.assignmentType].map(
      escapeCell
    )
  );
  const csv = [header, ...rows].map((row) => row.join(',')).join('\r\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const safeTagName = sanitizeForFilename(tagName || 'tag') || 'tag';
  link.download = `${safeTagName}-blocking-assignments.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const TagBlockersDialog: FunctionComponent<ITagBlockersDialogProps> = ({
  open,
  clientId,
  tagId,
  tagName,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<ITagBlockersResponse | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!open || !tagId || !clientId) {
      setResponse(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setLoadFailed(false);
    setResponse(null);
    API.load(`/clients/${clientId}/scenario_link_tags/${tagId}/blockers`)
      .then((data: ITagBlockersResponse) => {
        if (!cancelled) setResponse(data);
      })
      .catch(() => {
        if (!cancelled) {
          // Without this, a failed fetch rendered "0 assignments", which
          // reads as "safe to delete".
          setLoadFailed(true);
          enqueueSnackbar('Failed to load the tag’s blocking assignments', {
            variant: 'error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, tagId, clientId, enqueueSnackbar]);

  const items = response?.items ?? [];

  return (
    <PopupDialog
      title="Assignments Blocking"
      open={open}
      close={onClose}
      maxWidth="md"
      closeButtonLabel="Close"
      primaryButtons={
        items.length > 0
          ? [
              {
                id: 'download-blockers-csv',
                label: 'Download CSV',
                onClick: () => downloadBlockersCsv(tagName, items),
              },
            ]
          : []
      }
    >
      <Box>
        {isLoading ? (
          <CenteredSpinner />
        ) : (
          <>
            <Box
              sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1 }}
            >
              <Typography variant="body2" color="text.secondary" noWrap>
                Tag:
              </Typography>
              <OverflowTooltip
                value={tagName}
                variant="body2"
                sx={{ fontWeight: 600, maxWidth: 400 }}
              />
            </Box>
            {loadFailed ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Could not load the subscriptions that use this tag.
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This tag is used by {response?.total ?? 0} assignment
                {response?.total === 1 ? '' : 's'}. Remove all of them before
                deleting the tag.
                {/* The CSV is built from the returned items only, so when the
                    API truncated it must not be described as the full list. */}
                {response?.truncated
                  ? ` Showing the first ${items.length} results below - the CSV export contains only these first ${items.length} items, not the full list.`
                  : ''}
              </Typography>
            )}
            <Table
              id="tag-blockers-table"
              isLoading={false}
              isSuccess
              pagination={items.length > 0}
              rowCount={items.length}
              rowData={items}
              columnDefs={BLOCKER_COLUMNS}
              defaultColDef={{
                suppressAutoSize: true,
                suppressMenu: true,
                suppressSizeToFit: true,
              }}
            />
          </>
        )}
      </Box>
    </PopupDialog>
  );
};

export default TagBlockersDialog;
